<script setup lang="ts">
  import { computed, provide, useId } from 'vue';
  import { attributeContextKey } from '../Attribute/Attribute.context';
  import { AttributeAlign } from '../Attribute/Attribute.types';
  import { AttributeListSurface } from '../AttributeList/AttributeList.types';
  import { surfaceClasses } from '../AttributeList/AttributeList.css';
  import {
    actionClass,
    fillClass,
    rootClass,
    rowAlignClasses,
    rowClass,
    withDividersClass,
  } from './AttributeOverview.css';
  import type {
    AttributeOverviewProps,
    AttributeOverviewSlots,
  } from './AttributeOverview.types';

  const props = withDefaults(defineProps<AttributeOverviewProps>(), {
    surface: AttributeListSurface.None,
    align: AttributeAlign.Center,
    fill: false,
    withDividers: true,
  });

  defineSlots<AttributeOverviewSlots>();

  provide(attributeContextKey, { labelColumn: false });

  const uid = useId();
  const resolvedTestId = computed(
    () => props.testId ?? `ds-attribute-overview-${uid}`,
  );

  const rootClasses = computed(() => [
    rootClass,
    surfaceClasses[props.surface],
  ]);

  const rowClasses = computed(() => [
    rowClass,
    rowAlignClasses[props.align],
    { [fillClass]: props.fill, [withDividersClass]: props.withDividers },
  ]);
</script>

<template>
  <div
    :class="rootClasses"
    :role="ariaLabel ? 'group' : undefined"
    :aria-label="ariaLabel"
    :data-testid="resolvedTestId"
  >
    <dl :class="rowClasses" :data-testid="`${resolvedTestId}-content`">
      <slot />
    </dl>
    <div
      v-if="$slots.action"
      :class="actionClass"
      :data-testid="`${resolvedTestId}-action`"
    >
      <slot name="action" />
    </div>
  </div>
</template>
